import React, { useMemo } from "react";
import {
  buildKeyboardKeys,
  blackKeyAfterWhite,
  countWhiteKeys,
  KEYBOARD_61,
  type PianoKeyDef,
} from "../../lib/keyboardEngine";
import {
  degreeColor,
  fingerColor,
  type PracticeNoteVisualState,
} from "../../lib/practiceVisualTheme";
import { pitchClassColor } from "../../lib/pitchClassColors";
import { degreeRoman } from "../../lib/scaleDegreeLabels";
import type { PianoFingeredKey } from "../../lib/pianoFingeringEngine";

export interface PianoKeyboardProps {
  fingering?: PianoFingeredKey[];
  activeMidi?: number | null;
  highlightMidis?: Set<number>;
  noteStates?: Map<number, PracticeNoteVisualState>;
  showFingerRings?: boolean;
  showDegreeFill?: boolean;
  /** Un color distinto por clase de altura (Do, Re, Mi…). */
  colorByPitch?: boolean;
  /** Muestra nombre + grado en teclas de la escala (modo aprendizaje). */
  showMappedLabels?: boolean;
  dimUnmapped?: boolean;
  showHandZones?: boolean;
  onKeyClick?: (midi: number) => void;
  /** Dentro de PianoPracticePanel: sin borde exterior duplicado. */
  embedded?: boolean;
  className?: string;
}

const KEY_WHITE = "#FAFAF8";
const KEY_WHITE_BORDER = "#D8D4CC";
const KEY_BLACK = "#141312";
const KEY_BLACK_BORDER = "#0A0908";

export const PianoKeyboard: React.FC<PianoKeyboardProps> = ({
  fingering = [],
  activeMidi = null,
  highlightMidis,
  noteStates,
  showFingerRings = false,
  showDegreeFill = true,
  colorByPitch = false,
  showMappedLabels = false,
  dimUnmapped = false,
  showHandZones = false,
  onKeyClick,
  embedded = false,
  className = "",
}) => {
  const keys = useMemo(() => buildKeyboardKeys(KEYBOARD_61), []);
  const whiteCount = useMemo(() => countWhiteKeys(KEYBOARD_61), []);
  const fingerByMidi = useMemo(
    () => new Map(fingering.map((f) => [f.midi, f])),
    [fingering]
  );

  const whiteKeys = keys.filter((k) => k.kind === "white");
  const blackByAnchor = useMemo(() => {
    const map = new Map<number, number>();
    for (const w of whiteKeys) {
      const b = blackKeyAfterWhite(w.midi);
      if (b != null) map.set(w.midi, b);
    }
    return map;
  }, [whiteKeys]);

  const keyVisual = (key: PianoKeyDef, isBlack: boolean) => {
    const info = fingerByMidi.get(key.midi);
    const isActive = activeMidi === key.midi;
    const isHighlight = highlightMidis?.has(key.midi) ?? false;
    const state =
      noteStates?.get(key.midi) ?? (isActive ? "hit" : isHighlight ? "expected" : "idle");
    const mapped = Boolean(info);
    const emphasized = mapped || isActive || isHighlight;

    let fill: string;
    if (emphasized && colorByPitch) {
      fill = pitchClassColor(key.midi);
    } else if (emphasized && showDegreeFill && info?.degree) {
      fill = degreeColor(info.degree.replace("*", ""));
    } else if (isActive || isHighlight) {
      fill = "var(--bordona-accent, #E63946)";
    } else if (isBlack) {
      fill = KEY_BLACK;
    } else {
      fill = KEY_WHITE;
    }

    const finger = info?.finger;
    const ringColor = finger && showFingerRings ? fingerColor(finger) : undefined;
    const dimmed = dimUnmapped && !emphasized;

    return {
      fill,
      ringColor,
      dimmed,
      state,
      isActive,
      label: info
        ? `${key.label} · dedo ${info.finger}`
        : key.label,
    };
  };

  return (
    <div
      className={`relative w-full select-none ${className}`}
      style={{ aspectRatio: `${whiteCount * 1.12} / 4`, maxHeight: 132 }}
    >
      <div
        className={`relative h-full w-full overflow-hidden bg-[#D8D4CC] gap-px ${
          embedded ? "rounded-b-md" : "rounded-b-md border border-[#D8D4CC]"
        }`}
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${whiteCount}, minmax(0, 1fr))`,
        }}
      >
        {whiteKeys.map((w) => {
          const blackMidi = blackByAnchor.get(w.midi);
          const blackDef = blackMidi != null ? keys.find((k) => k.midi === blackMidi) : null;
          const wVis = keyVisual(w, false);
          const bVis = blackDef ? keyVisual(blackDef, true) : null;
          const wInfo = fingerByMidi.get(w.midi);
          const bInfo = blackDef ? fingerByMidi.get(blackDef.midi) : undefined;

          return (
            <div key={w.midi} className="relative h-full min-w-0">
              <button
                type="button"
                aria-label={wVis.label}
                title={wVis.label}
                onClick={() => onKeyClick?.(w.midi)}
                className="block w-full h-full transition-colors duration-75 rounded-b-[5px]"
                style={{
                  background: wVis.fill,
                  opacity: wVis.dimmed ? 0.4 : 1,
                  boxShadow: wVis.isActive
                    ? "0 0 0 2px var(--bordona-accent, #E63946)"
                    : undefined,
                  outline: wVis.ringColor ? `2px solid ${wVis.ringColor}` : undefined,
                  outlineOffset: -2,
                }}
              />

              {showMappedLabels && wInfo && (
                <span
                  className="absolute bottom-[20%] left-1/2 -translate-x-1/2 z-[2] pointer-events-none text-center leading-tight text-[#1a1816]"
                >
                  <span className="block text-[9px] font-sans font-bold">{w.label}</span>
                  <span className="block text-[8px] font-sans font-semibold text-[#5c5348]">
                    {degreeRoman(wInfo.degree.replace("*", ""))}
                  </span>
                </span>
              )}

              {blackDef && bVis && (
                <button
                  type="button"
                  aria-label={bVis.label}
                  title={bVis.label}
                  onClick={() => onKeyClick?.(blackDef.midi)}
                  className="absolute z-10 top-0 transition-colors duration-75 rounded-b-[3px]"
                  style={{
                    right: 0,
                    width: "62%",
                    height: "58%",
                    transform: "translateX(50%)",
                    background: bVis.fill,
                    opacity: bVis.dimmed ? 0.45 : 1,
                    border: `1px solid ${KEY_BLACK_BORDER}`,
                    boxShadow: bVis.isActive
                      ? "0 0 0 2px var(--bordona-accent, #E63946)"
                      : "0 0 1px rgba(0,0,0,0.5)",
                    outline: bVis.ringColor ? `2px solid ${bVis.ringColor}` : undefined,
                    outlineOffset: -1,
                  }}
                />
              )}
              {showMappedLabels && bInfo && blackDef && (
                <span className="absolute z-[11] top-[8%] right-0 translate-x-1/2 pointer-events-none text-center leading-tight text-[#f5f0eb] w-[62%]">
                  <span className="block text-[7px] font-sans font-bold">{blackDef.label}</span>
                  <span className="block text-[7px] font-sans opacity-90">
                    {degreeRoman(bInfo.degree.replace("*", ""))}
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
